import axios from 'axios';

const API_URL = '/api';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        const transactionToken = localStorage.getItem('transactionToken');
        if (transactionToken) {
            config.headers['X-Transaction-Token'] = transactionToken;
        }
        console.log('📡 API Request:', config.method.toUpperCase(), config.url);
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => {
        console.log('✅ API Response:', response.status, response.config?.url);
        return response;
    },
    (error) => {
        // FIXED: Add admin change password to exclusion list
        const isChangePassword = error.config?.url?.includes('/user/change-password');
        const isAdminChangePassword = error.config?.url?.includes('/admin/change-password');  // ADD THIS
        const isTransactionVerification = error.config?.url?.includes('/auth/verify-transaction-password');
        
        if (error.response?.data?.code === 'TRANSACTION_EXPIRED') {
            localStorage.removeItem('transactionToken');
            window.dispatchEvent(new CustomEvent('transactionExpired'));
        }
        
        // FIXED: Exclude admin change password from redirect
        if (error.response?.status === 401 && !isChangePassword && !isAdminChangePassword && !isTransactionVerification) {
            console.log('🔐 Unauthorized, redirecting to login...');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('transactionToken');
            window.location.href = '/login';
        }
        
        // For admin change password with 401, just return the error without redirect
        if (error.response?.status === 401 && isAdminChangePassword) {
            console.log('❌ Admin password change failed - incorrect current password');
        }
        
        return Promise.reject(error);
    }
);

// Auth APIs
export const authAPI = {
    login: (data) => api.post('/auth/login', data),
    getProfile: () => api.get('/auth/profile'),
    
    verifyTransactionPassword: async (data) => {
        const response = await api.post('/auth/verify-transaction-password', data);
        return response.data;
    },
    
    setTransactionPassword: async (data) => {
        const response = await api.post('/auth/set-transaction-password', data);
        return response.data;
    },
    
    checkFirstLogin: async () => {
        const response = await api.get('/auth/check-first-login');
        return response.data;
    },
    
    resetTransactionPassword: async (userId) => {
        const response = await api.post('/auth/reset-transaction-password', { userId });
        return response.data;
    }
};

// User APIs - ADD ALL ADMIN METHODS HERE
export const userAPI = {
    // Basic user operations
    createUser: (data) => api.post('/users/create', data),
    getDownline: () => api.get('/users/downline'),
    getUserDetails: (userId) => api.get(`/users/${userId}`),
    
    // ============ ADMIN METHODS ============
    getAllUsers: async (params) => {
        const response = await api.get('/admin/users', { params });
        return response.data;
    },
    
    changeAdminPassword: async (data) => {
        try {
            const response = await api.post('/admin/change-password', data);
            return response.data;
        } catch (error) {
            console.error('Change password API error:', error.response?.data);
            // Re-throw the error so the component can handle it
            throw error;
        }
    },
    
    getAdminStats: async (params) => {
        const response = await api.get('/admin/stats', { params });
        return response.data;
    },
    
    toggleUserStatus: async (userId) => {
        const response = await api.patch(`/admin/users/${userId}/toggle-status`);
        return response.data;
    },
    
    toggleBettingStatus: async (userId) => {
        const response = await api.patch(`/admin/users/${userId}/toggle-betting`);
        return response.data;
    },
    
    updateExposureLimit: async (userId, limit) => {
        const response = await api.patch(`/admin/users/${userId}/exposure-limit`, { limit });
        return response.data;
    },
    
    changeUserPassword: async (userId, password) => {
        const response = await api.patch(`/admin/users/${userId}/change-password`, { password });
        return response.data;
    },
    
    getRecentActivities: async (params) => {
        const response = await api.get('/admin/recent-activities', { params });
        return response.data;
    },
    
    // Downline management APIs
    getDownlineDetails: (userId, params) => api.get(`/users/downline/${userId}/details`, { params }),
    getDownlineWithdrawals: (userId, params) => api.get(`/users/downline/${userId}/withdrawals`, { params }),
    getDownlineTransactions: (userId, params) => api.get(`/users/downline/${userId}/transactions`, { params }),
    getDownlineBalances: (userId) => api.get(`/users/downline/${userId}/balances`),
    getDownlineOverview: (userId) => api.get(`/users/downline/${userId}/overview`)
};

export const userDashboardAPI = {
    getDashboardStats: () => api.get('/user/dashboard/stats'),
    getCurrentBets: (params) => api.get('/user/current-bets', { params }),
    changePassword: async (data) => {
        try {
            const response = await api.post('/user/change-password', data);
            return response.data;
        } catch (error) {
            console.error('Error changing password:', error);
            throw error;
        }
    },
    logout: () => api.post('/user/logout')
};

// Wallet APIs
export const walletAPI = {
    credit: (data) => api.post('/wallet/credit', data),
    debit: (data) => api.post('/wallet/debit', data),
    getTransactions: (userId, params) => api.get(`/wallet/transactions/${userId}`, { params }),
    getMyTransactions: (params) => api.get('/wallet/my-transactions', { params }),
    getRecentTransactions: (params) => api.get('/wallet/recent', { params })
};

// Betting APIs
export const bettingAPI = {
    getUserBets: (params) => api.get('/betting/user/bets', { params }),
    getCurrentBets: (params) => api.get('/betting/user/current-bets', { params }),
    getMarketPosition: (marketId) => api.get(`/betting/market-position/${marketId}`),
    placeBet: (data) => api.post('/betting/place-bet', data),
    
    getAllBets: async (params) => {
        const response = await api.get('/admin/bets', { params });
        return response.data;
    },
    
    getBetDetails: async (betId) => {
        const response = await api.get(`/admin/bets/${betId}`);
        return response.data;
    }
};

// Admin APIs
export const adminAPI = {
    getTransactions: (params) => api.get('/admin/transactions', { params }),
    getTransactionDetails: (transactionId) => api.get(`/admin/transactions/${transactionId}`),
    creditWallet: (data) => api.post('/admin/wallet/credit', data),
    debitWallet: (data) => api.post('/admin/wallet/debit', data),
        deleteUnsettledBets: async (marketId) => {
        const response = await api.delete(`/admin/bets/${marketId}`);
        return response.data;
    }
};

export default api;